// ProgramUsers.jsx

import React, { useState, useEffect, useMemo } from 'react';
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    CardTitle,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    FormGroup,
    Label,
    Input,
    Row,
} from 'reactstrap';
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Link } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import { ThreeCircles } from 'react-loader-spinner';
import Flatpickr from "react-flatpickr";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import firebase from "firebase/compat/app";
import "firebase/firestore";
import "firebase/auth";
import 'firebase/storage';
import { serverTimestamp, arrayUnion, arrayRemove } from 'firebase/firestore';
import { getDownloadURL, uploadBytesResumable, getStorage, ref } from "firebase/storage";

const ProgramUsers = () => {

    const storage = getStorage();

    // ---------------------------
    // STATE VARIABLES
    // ---------------------------
    const [initialLoad, setInitialLoad] = useState(true);
    const [allUsersList, setAllUsersList] = useState([]);
    const [fetchedData, setFetchedData] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // Action buttons states
    const [isCreating, setIsCreating] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isActivating, setIsActivating] = useState(false);

    const [updateUserId, setUpdateUserId] = useState('');
    const [updateUserIndex, setUpdateUserIndex] = useState(null);
    const [deleteUserId, setDeleteUserId] = useState('');
    const [deleteUserIndex, setDeleteUserIndex] = useState(null);
    const [enableUserId, setEnableUserId] = useState('');
    const [enableUserIndex, setEnableUserIndex] = useState(null);

    const [updatedImage, setUpdatedImage] = useState(null);
    const [uploadedImage, setUploadedImage] = useState(null);

    const [allTagPrograms, setAllTagPrograms] = useState([]);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [startDateFilter, setStartDateFilter] = useState(null);
    const [endDateFilter, setEndDateFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(''); // '', 'active', 'inactive'

    // Pagination
    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 10;

    // Modal Visibility States
    const [createModal, setCreateModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [activateModal, setActivateModal] = useState(false);

    // Reference to program data
    const userObject = JSON.parse(localStorage.getItem("userData"));
    const programObject = JSON.parse(localStorage.getItem("programData"));
    const programRef = firebase.firestore().collection('programs').doc(programObject.id);

    // ---------------------------
    // DOCUMENT TITLE
    // ---------------------------
    useEffect(() => {
        document.title = "Program Users | Fish-Trak - Partner's Dashboard";
    }, []);

    // ---------------------------
    // FETCH TAG PROGRAMS
    // ---------------------------
    const fetchTagPrograms = () => {
        firebase
            .firestore()
            .collection('programs')
            .orderBy('created_at', 'desc')
            .onSnapshot(
                querySnapshot => {
                    const newProgramsList = [];
                    querySnapshot.forEach(doc => {
                        const programItem = doc.data();
                        programItem.id = doc.id;

                        programItem.name = programItem.program_name;
                        programItem.region = programItem.program_region;
                        programItem.country = programItem.country_code;
                        programItem.type = programItem.program_type;
                        programItem.length = programItem.tag_length;
                        programItem.image = programItem.image_url;

                        const createdAt = programItem && programItem.created_at
                            ? programItem.created_at.toDate().toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                            : '';
                        programItem.created = createdAt;
                        const updatedAt = programItem && programItem.updated_at
                            ? programItem.updated_at.toDate().toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                            : '';
                        programItem.updated = updatedAt;

                        newProgramsList.push(programItem);
                    });
                    setAllTagPrograms(newProgramsList);
                },
                error => {
                    console.log("Error fetching tag programs:", error);
                }
            );
    };

    // ---------------------------
    // FETCH PROGRAM USERS WITH FILTERS
    // ---------------------------
    const fetchAllUsers = () => {
        let newQuery = firebase
            .firestore()
            .collection('users')
            .orderBy('created_at', 'desc')
            .where('partner_user', '==', true)
            .where('program_access', '==', programRef);

        // Apply status filter
        if (statusFilter === 'active') {
            newQuery = newQuery.where('active', '==', true);
        } else if (statusFilter === 'inactive') {
            newQuery = newQuery.where('active', '==', false);
        }

        // Apply date filters
        if (startDateFilter && endDateFilter) {
            newQuery = newQuery
                .where('created_at', '>=', firebase.firestore.Timestamp.fromDate(new Date(startDateFilter)))
                .where('created_at', '<=', firebase.firestore.Timestamp.fromDate(new Date(endDateFilter)));
        }
        else if (startDateFilter && !endDateFilter) {
            newQuery = newQuery
                .where('created_at', '>=', firebase.firestore.Timestamp.fromDate(new Date(startDateFilter)));
        }
        else if (!startDateFilter && endDateFilter) {
            newQuery = newQuery
                .where('created_at', '<=', firebase.firestore.Timestamp.fromDate(new Date(endDateFilter)));
        }

        newQuery.onSnapshot(
            (querySnapshot) => {
                const newUsersList = [];
                querySnapshot.forEach((doc) => {
                    const userItem = doc.data();
                    userItem.id = doc.id;

                    userItem.name = `${userItem.first_name} ${userItem.last_name}`;
                    userItem.email = userItem.email_address;
                    userItem.port = userItem.home_port;
                    const createdAt = userItem && userItem.created_at
                        ? userItem.created_at.toDate().toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                        : '';
                    userItem.created = createdAt;
                    const updatedAt = userItem && userItem.updated_at
                        ? userItem.updated_at.toDate().toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                        : '';
                    userItem.updated = updatedAt;
                    userItem.count = userItem.catch_count;
                    userItem.status = userItem.active;
                    userItem.link = `/user/details/${userItem.id}`;
                    userItem.mailtoLink = `mailto:${userItem.email_address}?subject=Fish-Trak Admin`;

                    newUsersList.push(userItem);
                });
                setAllUsersList(newUsersList);
                setEditModal(false);
                setCreateModal(false);
                setDeleteModal(false);
                setActivateModal(false);
                setIsUploading(false);
                setIsUpdating(false);
                setIsDeleting(false);
                setIsActivating(false);
                setIsCreating(false);
                setFetchedData(true);
            },
            (error) => {
                console.error("Error fetching program users:", error);
            }
        );
    };

    // ---------------------------
    // INITIAL DATA FETCHING
    // ---------------------------
    useEffect(() => {
        if (initialLoad) {
            setInitialLoad(false);
            fetchAllUsers();
            fetchTagPrograms();
        }

        if (fetchedData && allUsersList.length > 0) {
            setFetchedData(false);
            // No need to initialize list.js
        }
    }, [initialLoad, fetchedData, allUsersList.length]);

    // ---------------------------
    // SEARCH AND FILTER LOGIC
    // ---------------------------
    const filteredUsers = useMemo(() => {
        return allUsersList.filter(user => {
            const nameMatch = user.name.toLowerCase().includes(searchQuery.toLowerCase());
            const emailMatch = user.email.toLowerCase().includes(searchQuery.toLowerCase());
            const portMatch = user.port.toLowerCase().includes(searchQuery.toLowerCase());
            return nameMatch || emailMatch || portMatch;
        });
    }, [allUsersList, searchQuery]);

    // ---------------------------
    // PAGINATION CALCULATIONS
    // ---------------------------
    const pageCount = Math.ceil(filteredUsers.length / itemsPerPage);
    const displayedUsers = filteredUsers.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

    // ---------------------------
    // HANDLE PAGE CHANGE
    // ---------------------------
    const handlePageChange = ({ selected }) => {
        setCurrentPage(selected);
    };

    // ---------------------------
    // HANDLE APPLY FILTERS BUTTON
    // ---------------------------
    const handleApplyFilters = () => {
        fetchAllUsers();
        setCurrentPage(0); // Reset to first page when filters are applied
    };

    // ---------------------------
    // HANDLE DOWNLOAD CSV
    // ---------------------------
    const handleDownloadCSV = async () => {
        try {
            let csvQuery = firebase
                .firestore()
                .collection('users')
                .orderBy('created_at', 'desc')
                .where('partner_user', '==', true)
                .where('program_access', '==', programRef);

            // Apply status filter
            if (statusFilter === 'active') {
                csvQuery = csvQuery.where('active', '==', true);
            } else if (statusFilter === 'inactive') {
                csvQuery = csvQuery.where('active', '==', false);
            }

            // Apply date filters
            if (startDateFilter && endDateFilter) {
                csvQuery = csvQuery
                    .where('created_at', '>=', firebase.firestore.Timestamp.fromDate(new Date(startDateFilter)))
                    .where('created_at', '<=', firebase.firestore.Timestamp.fromDate(new Date(endDateFilter)));
            }
            else if (startDateFilter && !endDateFilter) {
                csvQuery = csvQuery
                    .where('created_at', '>=', firebase.firestore.Timestamp.fromDate(new Date(startDateFilter)));
            }
            else if (!startDateFilter && endDateFilter) {
                csvQuery = csvQuery
                    .where('created_at', '<=', firebase.firestore.Timestamp.fromDate(new Date(endDateFilter)));
            }

            const snapshot = await csvQuery.get();
            const csvRows = [];

            // CSV header
            csvRows.push([
                'User ID',
                'First Name',
                'Last Name',
                'Email',
                'Home Port',
                'Created Date',
                'Created Time',
                'Updated Date',
                'Updated Time',
                'Active?',
                'Catch Count',
                'Recatch Count',
                'Species Count'
            ].join(','));

            snapshot.forEach((doc) => {
                const data = doc.data();
                const createdAt = data.created_at
                    ? data.created_at.toDate().toLocaleString()
                    : '';
                const updatedAt = data.updated_at
                    ? data.updated_at.toDate().toLocaleString()
                    : '';
                const row = [
                    doc.id,
                    (data.first_name || '').replace(/,/g, ''),
                    (data.last_name || '').replace(/,/g, ''),
                    (data.email_address || '').replace(/,/g, ''),
                    (data.home_port || '').replace(/,/g, ''),
                    createdAt,
                    updatedAt,
                    data.active ? 'Yes' : 'No',
                    typeof data.catch_count !== 'undefined' ? data.catch_count : '',
                    typeof data.recatch_count !== 'undefined' ? data.recatch_count : '',
                    typeof data.species_count !== 'undefined' ? data.species_count : ''
                ];
                csvRows.push(row.join(','));
            });

            const csvString = csvRows.join('\n');
            const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'filtered_program_users.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (error) {
            console.error('Error generating CSV:', error);
            toast.error('Could not download CSV. Please try again.');
        }
    };

    // ---------------------------
    // CRUD OPERATIONS
    // ---------------------------

    // Handle Create User Form Submission
    const handleCreateUser = (event) => {
        event.preventDefault();
        submitCreateForm(event);
    };

    const submitCreateForm = async (event) => {
        setIsUploading(true);
        setIsCreating(true);
        event.preventDefault();

        if (uploadedImage) {
            await uploadImage(event);
        }
        else {
            await createUser(event);
        }
    };

    const uploadImage = async (event) => {
        event.preventDefault();
        const emailAddress = event.target.email_address.value.replace(/[^a-zA-Z0-9+-_~!#$%&'./=^|{}"]/g, '');
        const timeStamp = Date.now();

        const imageFileName = `user-avatars/${emailAddress}-${timeStamp}`;

        const storageRef = ref(storage, imageFileName);

        const uploadTask = uploadBytesResumable(storageRef, uploadedImage);
        uploadTask.on(
            "state_changed",
            (snapshot) => {
                const progress =
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                console.log("Upload is " + progress + "% done");
            },
            (error) => {
                console.log(error);
                setIsUploading(false);
                setIsCreating(false);
                toast.error('Could not upload user avatar. Please try again.');
            },
            () => {
                getDownloadURL(uploadTask.snapshot.ref).then((downloadedURL) => {
                    createUser(event, downloadedURL);
                });
            }
        );
    };

    const generateRandomPassword = () => {
        const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()";
        const length = 12;
        return Array.from({ length }, () => charset[Math.floor(Math.random() * charset.length)]).join("");
    };

    const createUser = async (event, downloadedURL = null) => {
        setIsUploading(false);
        setIsCreating(true);

        const emailAddress = event.target.email_address.value.replace(/[^a-zA-Z0-9+-_~!#$%&@'./=^|{}"]/g, '');
        const userPassword = generateRandomPassword();
        const locationGeo = new firebase.firestore.GeoPoint(
            parseFloat(event.target.home_port_lat.value.replace(/[^0-9.-]/g, '')),
            parseFloat(event.target.home_port_lng.value.replace(/[^0-9.-]/g, ''))
        );

        const avatarPlaceholderImage = 'https://firebasestorage.googleapis.com/v0/b/fish-trak-app.appspot.com/o/program-logos%2Ffish-trak-program-logo.png?alt=media&token=8ef8bf14-1c21-4dd1-8ffc-6f860f41dfa6';

        try {
            const userCredential = await firebase.auth().createUserWithEmailAndPassword(emailAddress, userPassword);
            await userCredential.user.sendEmailVerification();
            const uid = userCredential.user.uid;

            // Prepare user data
            const userData = {
                id: uid,
                account_type: 1,
                active: true,
                approved: true,
                verified: false,
                onboarded: false,
                catch_count: 0,
                created_at: serverTimestamp(),
                recatch_count: 0,
                species_count: 0,
                signup_location: locationGeo,
                text_messages: true,
                updated_at: serverTimestamp(),
                privacy_mode: false,
                home_port_geo: locationGeo,
                email_address: emailAddress,
                email_updates: true,
                text_messages: true,
                recatch_alerts: true,
                notifications: true,
                admin_user: false,
                partner_user: true,
                program_access: programRef,

                first_name: event.target.first_name.value.replace(/[^a-zA-Z&-,.' ]/g, ''),
                last_name: event.target.last_name.value.replace(/[^a-zA-Z&-,.' ]/g, ''),
                mobile_number: event.target.mobile_number.value.replace(/[^0-9+]/g, ''),
                home_port: event.target.home_port.value.replace(/[^a-zA-Z&()-+',./' ]/g, ''),
                country_code: event.target.user_country.value.replace(/[^a-zA-Z]/g, ''),
                metric_system: JSON.parse(event.target.units.value),
                image_url: downloadedURL ? downloadedURL : avatarPlaceholderImage
            };

            // If Partner Access is enabled, assign program_access
            if (userData.partner_user && event.target.tag_program.value) {
                userData.program_access = firebase.firestore().collection('programs').doc(event.target.tag_program.value);
            }

            // Add user to Firestore
            await firebase.firestore().collection('users').doc(uid).set(userData);

            // Send Password Reset Email
            await firebase.auth().sendPasswordResetEmail(emailAddress);

            toast.success('The user has been created successfully!');
            fetchAllUsers();
            setCreateModal(false);
            setIsCreating(false);
        } catch (error) {
            console.error("Error creating user:", error);
            setIsUploading(false);
            setIsCreating(false);
            if (error.code === 'auth/email-already-in-use') {
                toast.error('That email address is already registered. Please use a different email.');
            }
            else {
                toast.error('Could not create account. Please try again.');
            }
        }
    };

    // Handle Update User Form Submission
    const handleUpdateUser = (event) => {
        event.preventDefault();
        submitUpdateForm(event);
    };

    const submitUpdateForm = async (event) => {
        setIsUploading(true);
        setIsUpdating(true);
        event.preventDefault();

        if (updatedImage) {
            await updateImage(event);
        }
        else {
            await updateUser(event);
        }
    };

    const updateImage = async (event) => {
        event.preventDefault();
        const imageFileName = `user-avatars/${allUsersList[updateUserIndex].id}`;
        const storageRef = ref(storage, imageFileName);

        const uploadTask = uploadBytesResumable(storageRef, updatedImage);
        uploadTask.on(
            "state_changed",
            (snapshot) => {
                const progress =
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                console.log("Upload is " + progress + "% done");
            },
            (error) => {
                console.log(error);
                setIsUpdating(false);
                setIsUploading(false);
                toast.error('Could not update user avatar. Please try again.');
            },
            () => {
                getDownloadURL(uploadTask.snapshot.ref).then((downloadedURL) => {
                    updateUser(event, downloadedURL);
                });
            }
        );
    };

    const updateUser = async (event, downloadedURL = null) => {
        // Prepare user data
        const userData = {
            updated_at: serverTimestamp(),
        };

        // User information
        if (event.target.first_name.value !== allUsersList[updateUserIndex].first_name) {
            userData['first_name'] = event.target.first_name.value.replace(/[^a-zA-Z&-,.' ]/g, '');
        }
        if (event.target.last_name.value !== allUsersList[updateUserIndex].last_name) {
            userData['last_name'] = event.target.last_name.value.replace(/[^a-zA-Z&-,.' ]/g, '');
        }

        // Contact details
        if (event.target.email_address.value !== allUsersList[updateUserIndex].email_address) {
            userData['email_address'] = event.target.email_address.value.replace(/[^a-zA-Z0-9+-_~!#$%&@'./=^|{}"]/g, '');
        }
        if (event.target.mobile_number.value !== allUsersList[updateUserIndex].mobile_number) {
            userData['mobile_number'] = event.target.mobile_number.value.replace(/[^0-9+]/g, '');
        }

        // Location data
        if (event.target.home_port.value !== allUsersList[updateUserIndex].home_port) {
            userData['home_port'] = event.target.home_port.value.replace(/[^a-zA-Z&()-+',./' ]/g, '');
        }
        if (
            parseFloat(event.target.home_port_lat.value) !== allUsersList[updateUserIndex].home_port_geo.latitude ||
            parseFloat(event.target.home_port_lng.value) !== allUsersList[updateUserIndex].home_port_geo.longitude
        ) {
            userData['home_port_geo'] = new firebase.firestore.GeoPoint(
                parseFloat(event.target.home_port_lat.value.replace(/[^0-9.-]/g, '')),
                parseFloat(event.target.home_port_lng.value.replace(/[^0-9.-]/g, ''))
            );
        }
        if (event.target.user_country.value !== allUsersList[updateUserIndex].country_code) {
            userData['country_code'] = event.target.user_country.value.replace(/[^a-zA-Z]/g, '');
        }

        // Metric counts 
        if (parseInt(event.target.catch_count.value) !== allUsersList[updateUserIndex].catch_count) {
            userData['catch_count'] = parseInt(event.target.catch_count.value) || 0;
        }
        if (parseInt(event.target.recatch_count.value) !== allUsersList[updateUserIndex].recatch_count) {
            userData['recatch_count'] = parseInt(event.target.recatch_count.value) || 0;
        }
        if (parseInt(event.target.species_count.value) !== allUsersList[updateUserIndex].species_count) {
            userData['species_count'] = parseInt(event.target.species_count.value) || 0;
        }

        // Account Status
        if (event.target.units.value !== String(allUsersList[updateUserIndex].metric_system)) {
            userData['metric_system'] = JSON.parse(event.target.units.value);
        }
        if (event.target.active.checked !== allUsersList[updateUserIndex].active) {
            userData['active'] = event.target.active.checked;
        }
        if (event.target.approved.checked !== allUsersList[updateUserIndex].approved) {
            userData['approved'] = event.target.approved.checked;
        }

        // Notifications
        if (event.target.email_updates.checked !== allUsersList[updateUserIndex].email_updates) {
            userData['email_updates'] = event.target.email_updates.checked;
        }
        if (event.target.text_messages.checked !== allUsersList[updateUserIndex].text_messages) {
            userData['text_messages'] = event.target.text_messages.checked;
        }
        if (event.target.recatch_alerts.checked !== allUsersList[updateUserIndex].recatch_alerts) {
            userData['recatch_alerts'] = event.target.recatch_alerts.checked;
        }
        if (event.target.push_notifications.checked !== allUsersList[updateUserIndex].notifications) {
            userData['notifications'] = event.target.push_notifications.checked;
        }

        // Permissions
        // if (event.target.partner_access.checked !== allUsersList[updateUserIndex].partner_user) {
        //     userData['partner_user'] = event.target.partner_access.checked;
        // }
        if (event.target.privacy_mode.checked !== allUsersList[updateUserIndex].privacy_mode) {
            userData['privacy_mode'] = event.target.privacy_mode.checked;
        }

        // Program Access
        // if (event.target.partner_access.checked) {
        //     if (event.target.tag_program.value !== (allUsersList[updateUserIndex].program_access?.id || '')) {
        //         userData['program_access'] = firebase.firestore().collection('programs').doc(event.target.tag_program.value);
        //     }
        // } else {
        //     userData['program_access'] = null;
        // }

        // New Image URL
        if (downloadedURL) {
            userData['image_url'] = downloadedURL;
        }

        try {
            const userRef = firebase.firestore().collection('users').doc(allUsersList[updateUserIndex].id);
            await userRef.update(userData);

            // Handle program_access updates
            if (event.target.partner_access.checked && event.target.tag_program.value) {
                // Add to new program's program_managers
                const newProgramRef = firebase.firestore().collection('programs').doc(event.target.tag_program.value);
                await newProgramRef.update({
                    program_managers: arrayUnion(userRef)
                });

                // If previously had a different program_access, remove from old
                if (allUsersList[updateUserIndex].program_access && allUsersList[updateUserIndex].program_access.id !== event.target.tag_program.value) {
                    await allUsersList[updateUserIndex].program_access.update({
                        program_managers: arrayRemove(userRef)
                    });
                }
            } else if (!event.target.partner_access.checked && allUsersList[updateUserIndex].program_access) {
                // Remove from old program's program_managers
                await allUsersList[updateUserIndex].program_access.update({
                    program_managers: arrayRemove(userRef)
                });
            }

            toast.success('The user has been updated successfully!');
            fetchAllUsers();
            setIsUpdating(false);
            setEditModal(false);
        } catch (error) {
            console.error("Error updating user:", error);
            setIsUpdating(false);
            toast.error('Could not update user entry. Please try again.');
        }
    };

    // ---------------------------
    // HANDLE ENABLE USER
    // ---------------------------
    const activateUser = async () => {
        setIsActivating(true);

        try {
            const userRef = firebase.firestore().collection('users').doc(allUsersList[enableUserIndex].id);
            await userRef.update({ active: true });
            toast.success('The user account has been activated successfully!');
            fetchAllUsers();
            setEnableUserId('');
            setEnableUserIndex(null);
            setIsActivating(false);
            setActivateModal(false);
        } catch (error) {
            console.error("Error activating user:", error);
            setIsActivating(false);
            toast.error('Could not activate user. Please try again.');
        }
    };

    // ---------------------------
    // HANDLE DISABLE USER
    // ---------------------------
    const disableUser = async () => {
        setIsDeleting(true);

        try {
            const userRef = firebase.firestore().collection('users').doc(allUsersList[deleteUserIndex].id);
            await userRef.update({ active: false });
            toast.success('The user account has been disabled successfully!');
            fetchAllUsers();
            setDeleteUserId('');
            setDeleteUserIndex(null);
            setIsDeleting(false);
            setDeleteModal(false);
        } catch (error) {
            console.error("Error disabling user:", error);
            setIsDeleting(false);
            toast.error('Could not disable user. Please try again.');
        }
    };

    // ---------------------------
    // CRUD MODAL TOGGLES
    // ---------------------------
    const toggleDeleteModal = (userId = '', userIndex = null) => {
        setDeleteUserId(userId);
        setDeleteUserIndex(userIndex);
        setDeleteModal(!deleteModal);
    };

    const toggleActivateModal = (userId = '', userIndex = null) => {
        setEnableUserId(userId);
        setEnableUserIndex(userIndex);
        setActivateModal(!activateModal);
    };

    const toggleEditModal = (userId = '', userIndex = null) => {
        setUpdateUserId(userId);
        setUpdateUserIndex(userIndex);
        setEditModal(!editModal);
    };

    const toggleCreateModal = () => {
        setCreateModal(!createModal);
    };

    // ---------------------------
    // COMPONENT RETURN
    // ---------------------------
    return (
        <React.Fragment>
            <ToastContainer />
            <div className="page-content">
                <Container fluid>

                    <CardTitle>My Program</CardTitle>

                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader>
                                    <h4 className="card-title mb-0">Access Users</h4>
                                </CardHeader>

                                <CardBody>
                                    {/* Filters and Actions */}
                                    <Row className="g-4 mb-3">
                                        <Col sm="auto">
                                            <div className="d-flex gap-1">
                                                <Button
                                                    color="success"
                                                    onClick={() => toggleCreateModal()}
                                                    id="create-btn"
                                                >
                                                    <i className="ri-add-line align-bottom me-1"></i> Add User
                                                </Button>
                                            </div>
                                        </Col>
                                        <Col className="d-flex align-items-start">
                                                <Input
                                                    type="search"
                                                    name="search"
                                                    id="search"
                                                    placeholder="Search Users..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                />
                                        </Col>
                                        <Col sm="2" md="2">
                                                <Flatpickr
                                                    id="start-date"
                                                    className="form-control"
                                                    options={{ dateFormat: "Y-m-d", maxDate: endDateFilter ? endDateFilter : undefined }}
                                                    placeholder="Start Date"
                                                    value={startDateFilter}
                                                    onChange={(dates) => setStartDateFilter(dates[0] || null)}
                                                />
                                        </Col>
                                        <Col sm="2" md="2">
                                                <Flatpickr
                                                    id="end-date"
                                                    className="form-control"
                                                    options={{ dateFormat: "Y-m-d", minDate: startDateFilter ? startDateFilter : undefined }}
                                                    placeholder="End Date"
                                                    value={endDateFilter}
                                                    onChange={(dates) => setEndDateFilter(dates[0] || null)}
                                                />
                                        </Col>
                                        <Col sm="2" md="2">
                                                <Input
                                                    type="select"
                                                    name="status_filter"
                                                    id="status-filter"
                                                    value={statusFilter}
                                                    onChange={(e) => setStatusFilter(e.target.value)}
                                                >
                                                    <option value="">All Users</option>
                                                    <option value="active">Active</option>
                                                    <option value="inactive">Disabled</option>
                                                </Input>
                                        </Col>
                                        <Col sm="1" md="1" className="d-flex align-items-end">
                                            <Button
                                                color="secondary"
                                                onClick={handleApplyFilters}
                                                style={{ width: '100%' }}
                                            >
                                                <i className="mdi mdi-filter"></i>
                                            </Button>
                                        </Col>
                                        <Col sm="1" md="1" className="d-flex align-items-end">
                                            <Button
                                                color="primary"
                                                onClick={handleDownloadCSV}
                                                style={{ width: '100%' }}
                                            >
                                                <i className="mdi mdi-download"></i>
                                            </Button>
                                        </Col>
                                    </Row>
                                    {/* End Filters and Actions */}

                                    {/* Users Table */}
                                    <div className="table-responsive table-card mt-3 mb-1">
                                        {(!isUpdating && !isDeleting && !isActivating && !isCreating) && (
                                            <table className="table align-middle table-nowrap">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th scope="col" style={{ width: "50px" }}>View</th>
                                                        <th scope="col">Image</th>
                                                        <th scope="col">User Name</th>
                                                        <th scope="col">Email Address</th>
                                                        <th scope="col">User Location</th>
                                                        <th scope="col">Created Date</th>
                                                        <th scope="col">Last Active</th>
                                                        <th scope="col">Status</th>
                                                        <th scope="col">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {displayedUsers.length > 0 ? displayedUsers.map((user, index) => (
                                                        <tr key={user.id}>
                                                            <th scope="row">
                                                                <Link to={user.link} className="btn btn-sm btn-success" title="View User" style={{ backgroundColor: "purple" }}>
                                                                    <i className="mdi mdi-eye-outline"></i>
                                                                </Link>
                                                            </th>
                                                            <td>
                                                                {user.image_url ? (
                                                                    <img
                                                                        className="avatar-sm rounded-circle"
                                                                        alt={user.name}
                                                                        src={user.image_url}
                                                                    />
                                                                ) : (
                                                                    <span>No Image</span>
                                                                )}
                                                            </td>
                                                            <td>{user.name}</td>
                                                            <td>
                                                                <a href={user.mailtoLink} className="email-link">
                                                                    {user.email}
                                                                </a>
                                                            </td>
                                                            <td>{user.port}</td>
                                                            <td>{user.created}</td>
                                                            <td>{user.updated}</td>
                                                            <td>
                                                                {user.status ? (
                                                                    <span className="badge badge-soft-success text-uppercase">Active</span>
                                                                ) : (
                                                                    <span className="badge badge-soft-danger text-uppercase">Disabled</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div className="d-flex gap-2">
                                                                    {/* Email Button */}
                                                                    <Button
                                                                        color="primary"
                                                                        size="sm"
                                                                        onClick={() => window.location.href = user.mailtoLink}
                                                                        title="Email User"
                                                                    >
                                                                        <i className="mdi mdi-email-send"></i>
                                                                    </Button>
                                                                    {/* Edit Button */}
                                                                    <Button
                                                                        color="warning"
                                                                        size="sm"
                                                                        onClick={() => toggleEditModal(user.id, index)}
                                                                        title="Edit User"
                                                                    >
                                                                        <i className="mdi mdi-square-edit-outline"></i>
                                                                    </Button>
                                                                    {/* Disable/Activate Button */}
                                                                    {user.active ? (
                                                                        <Button
                                                                            color="danger"
                                                                            size="sm"
                                                                            onClick={() => toggleDeleteModal(user.id, index)}
                                                                            title="Disable User"
                                                                        >
                                                                            <i className="mdi mdi-account-off-outline"></i>
                                                                        </Button>
                                                                    ) : (
                                                                        <Button
                                                                            style={{ backgroundColor: "green" }}
                                                                            size="sm"
                                                                            onClick={() => toggleActivateModal(user.id, index)}
                                                                            title="Activate User"
                                                                        >
                                                                            <i className="mdi mdi-account-check-outline"></i>
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )) : (
                                                        <tr>
                                                            <td colSpan="9" className="text-center">No Users Found!</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        )}

                                        {/* Loader */}
                                        {(isUpdating || isDeleting || isActivating || isCreating) && (
                                            <div className="d-flex justify-content-center my-5">
                                                <ThreeCircles
                                                    visible={isUpdating || isDeleting || isActivating || isCreating}
                                                    height="100"
                                                    width="100"
                                                    color="#4192C3"
                                                    ariaLabel="three-circles-loading"
                                                    wrapperStyle={{ margin: "0 auto" }}
                                                    wrapperClass="justify-content-center"
                                                />
                                            </div>
                                        )}

                                        {/* No Results Message */}
                                        {filteredUsers.length === 0 && !isUpdating && !isDeleting && !isActivating && !isCreating && (
                                            <div className="noresult">
                                                <div className="text-center">
                                                    <h5 className="mt-2">No Users Found!</h5>
                                                    <p className="text-muted mb-0">We could not find any users.</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {/* End Users Table */}

                                    {/* Pagination Controls */}
                                    {pageCount > 1 && (
                                        <div className="d-flex justify-content-end">
                                            <ReactPaginate
                                                previousLabel={"← Previous"}
                                                nextLabel={"Next →"}
                                                pageCount={pageCount}
                                                onPageChange={handlePageChange}
                                                containerClassName={"pagination"}
                                                previousLinkClassName={"page-link"}
                                                nextLinkClassName={"page-link"}
                                                disabledClassName={"disabled"}
                                                activeClassName={"active"}
                                                pageLinkClassName={"page-link"}
                                                breakLabel={"..."}
                                                breakClassName={"page-item"}
                                                breakLinkClassName={"page-link"}
                                            />
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* --------------------------- */}
            {/* Modals */}
            {/* --------------------------- */}

            {/* Edit Modal */}
            <Modal isOpen={editModal} toggle={() => toggleEditModal()} centered>
                {!isUpdating && allUsersList[updateUserIndex] && (
                    <form className="tablelist-form" onSubmit={handleUpdateUser}>
                        <ModalHeader toggle={() => toggleEditModal()}>Edit User</ModalHeader>
                        <ModalBody>
                            {/* User Information */}
                            <FormGroup>
                                <Label htmlFor="edit-first-name">First Name*</Label>
                                <Input
                                    type="text"
                                    name="first_name"
                                    id="edit-first-name"
                                    placeholder="First Name"
                                    required
                                    defaultValue={allUsersList[updateUserIndex].first_name}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label htmlFor="edit-last-name">Last Name*</Label>
                                <Input
                                    type="text"
                                    name="last_name"
                                    id="edit-last-name"
                                    placeholder="Last Name"
                                    required
                                    defaultValue={allUsersList[updateUserIndex].last_name}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label htmlFor="edit-email">Email Address*</Label>
                                <Input
                                    type="email"
                                    name="email_address"
                                    id="edit-email"
                                    placeholder="email@example.com"
                                    required
                                    defaultValue={allUsersList[updateUserIndex].email_address}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label htmlFor="edit-mobile-number">Mobile Number*</Label>
                                <Input
                                    type="tel"
                                    name="mobile_number"
                                    id="edit-mobile-number"
                                    placeholder="+14151234567"
                                    required
                                    defaultValue={allUsersList[updateUserIndex].mobile_number}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label htmlFor="edit-avatar">User Avatar*</Label>
                                <Row>
                                    <Col xs="4">
                                        {allUsersList[updateUserIndex].image_url ? (
                                            <img
                                                className="avatar-sm rounded-circle"
                                                alt={allUsersList[updateUserIndex].name}
                                                src={allUsersList[updateUserIndex].image_url}
                                            />
                                        ) : (
                                            <span>No Image</span>
                                        )}
                                    </Col>
                                    <Col xs="8">
                                        <Input
                                            type="file"
                                            name="image_link"
                                            id="edit-avatar"
                                            className="form-control"
                                            onChange={(e) => { setUpdatedImage(e.target.files[0]); }}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>
                            <FormGroup>
                                <Label htmlFor="edit-home-port">Home Port Location*</Label>
                                <Input
                                    type="text"
                                    name="home_port"
                                    id="edit-home-port"
                                    placeholder="Toms River"
                                    required
                                    defaultValue={allUsersList[updateUserIndex].home_port}
                                />
                            </FormGroup>
                            <Row>
                                <Col>
                                    <FormGroup>
                                        <Label htmlFor="edit-home-port-lat">Home Port Latitude*</Label>
                                        <Input
                                            type="number"
                                            name="home_port_lat"
                                            id="edit-home-port-lat"
                                            placeholder="Enter Latitude*"
                                            step="any"
                                            required
                                            defaultValue={allUsersList[updateUserIndex].home_port_geo.latitude}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col>
                                    <FormGroup>
                                        <Label htmlFor="edit-home-port-lng">Home Port Longitude*</Label>
                                        <Input
                                            type="number"
                                            name="home_port_lng"
                                            id="edit-home-port-lng"
                                            placeholder="Enter Longitude*"
                                            step="any"
                                            required
                                            defaultValue={allUsersList[updateUserIndex].home_port_geo.longitude}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <FormGroup>
                                <Label htmlFor="edit-user-country">User Country*</Label>
                                <Input
                                    type="select"
                                    name="user_country"
                                    id="edit-user-country"
                                    required
                                    defaultValue={allUsersList[updateUserIndex].country_code}
                                >
                                    <option value="">Select User Country</option>
                                    <option value="USA">United States</option>
                                    <option value="CAN">Canada</option>
                                    {/* Add more countries as needed */}
                                </Input>
                            </FormGroup>

                            {/* Units System */}
                            <FormGroup>
                                <Label>Units System*</Label>
                                <div>
                                    <FormGroup check inline>
                                        <Label check>
                                            <Input
                                                type="radio"
                                                name="units"
                                                value="true"
                                                defaultChecked={allUsersList[updateUserIndex].metric_system}
                                            />{' '}
                                            Metric
                                        </Label>
                                    </FormGroup>
                                    <FormGroup check inline>
                                        <Label check>
                                            <Input
                                                type="radio"
                                                name="units"
                                                value="false"
                                                defaultChecked={!allUsersList[updateUserIndex].metric_system}
                                            />{' '}
                                            Imperial
                                        </Label>
                                    </FormGroup>
                                </div>
                            </FormGroup>

                            {/* Account Status */}
                            <FormGroup>
                                <Label>Account Status*</Label>
                                <div>
                                    <FormGroup check inline>
                                        <Label check>
                                            <Input
                                                type="checkbox"
                                                name="active"
                                                defaultChecked={allUsersList[updateUserIndex].active}
                                            />{' '}
                                            Activated
                                        </Label>
                                    </FormGroup>
                                    <FormGroup check inline>
                                        <Label check>
                                            <Input
                                                type="checkbox"
                                                name="approved"
                                                defaultChecked={allUsersList[updateUserIndex].approved}
                                            />{' '}
                                            Approved
                                        </Label>
                                    </FormGroup>
                                </div>
                            </FormGroup>

                            {/* Notifications */}
                            <FormGroup>
                                <Label>Notifications*</Label>
                                <div>
                                    <FormGroup check inline>
                                        <Label check>
                                            <Input
                                                type="checkbox"
                                                name="email_updates"
                                                defaultChecked={allUsersList[updateUserIndex].email_updates}
                                            />{' '}
                                            Email Updates
                                        </Label>
                                    </FormGroup>
                                    <FormGroup check inline>
                                        <Label check>
                                            <Input
                                                type="checkbox"
                                                name="text_messages"
                                                defaultChecked={allUsersList[updateUserIndex].text_messages}
                                            />{' '}
                                            Text Messages
                                        </Label>
                                    </FormGroup>
                                    <FormGroup check inline>
                                        <Label check>
                                            <Input
                                                type="checkbox"
                                                name="recatch_alerts"
                                                defaultChecked={allUsersList[updateUserIndex].recatch_alerts}
                                            />{' '}
                                            Recatch Alerts
                                        </Label>
                                    </FormGroup>
                                    <FormGroup check inline>
                                        <Label check>
                                            <Input
                                                type="checkbox"
                                                name="push_notifications"
                                                defaultChecked={allUsersList[updateUserIndex].notifications}
                                            />{' '}
                                            Push Notifications
                                        </Label>
                                    </FormGroup>
                                </div>
                            </FormGroup>

                            {/* Permissions */}
                            <FormGroup>
                                <Label>Permissions*</Label>
                                <div>
                                    {/*<FormGroup check inline>
                                        <Label check>
                                            <Input
                                                type="checkbox"
                                                name="partner_access"
                                                defaultChecked={allUsersList[updateUserIndex].partner_user}
                                            />{' '}
                                            Partner Access
                                        </Label>
                                    </FormGroup>*/}
                                    <FormGroup check inline>
                                        <Label check>
                                            <Input
                                                type="checkbox"
                                                name="privacy_mode"
                                                defaultChecked={allUsersList[updateUserIndex].privacy_mode}
                                            />{' '}
                                            Privacy Mode
                                        </Label>
                                    </FormGroup>
                                </div>
                            </FormGroup>

                            {/* Partner Tag Program */}
                            {/*allUsersList[updateUserIndex].partner_user && (
                                <FormGroup>
                                    <Label htmlFor="edit-tag-program">Partner Tag Program*</Label>
                                    <Input
                                        type="select"
                                        name="tag_program"
                                        id="edit-tag-program"
                                        required
                                        defaultValue={allUsersList[updateUserIndex].program_access?.id || ''}
                                    >
                                        <option value="">Select Tag Program</option>
                                        {allTagPrograms.map((prog) => (
                                            <option key={prog.id} value={prog.id}>
                                                {prog.name}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            )*/}

                            {/* Image URL */}
                            {updatedImage && (
                                <FormGroup>
                                    <Label htmlFor="edit-avatar-preview">Avatar Preview:</Label>
                                    <img
                                        className="avatar-sm rounded-circle"
                                        alt="Avatar Preview"
                                        src={URL.createObjectURL(updatedImage)}
                                    />
                                </FormGroup>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <div className="hstack gap-2 justify-content-end">
                                <Button
                                    type="button"
                                    color="secondary"
                                    onClick={() => toggleEditModal()}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    color="success"
                                    disabled={isUploading}
                                >
                                    Update
                                </Button>
                            </div>
                        </ModalFooter>
                    </form>
                )}
                {/* Loader for Update */}
                {isUpdating && (
                    <ModalBody>
                        <div className="d-flex justify-content-center my-5">
                            <ThreeCircles
                                visible={isUpdating}
                                height="100"
                                width="100"
                                color="#4192C3"
                                ariaLabel="three-circles-loading"
                                wrapperStyle={{ margin: "0 auto" }}
                                wrapperClass="justify-content-center"
                            />
                        </div>
                    </ModalBody>
                )}
            </Modal>

            {/* Delete (Disable) Modal */}
            <Modal isOpen={deleteModal} toggle={() => toggleDeleteModal()} centered>
                {!isDeleting && (
                    <ModalBody>
                        <div className="mt-2 text-center">
                            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
                                <h4>Disable User's Account</h4>
                                <p className="text-muted mx-4 mb-0">Are you sure you want to disable this user?</p>
                            </div>
                        </div>
                        <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
                            <Button
                                type="button"
                                color="light"
                                onClick={() => toggleDeleteModal()}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                color="danger"
                                onClick={async () => {
                                    await disableUser();
                                    toggleDeleteModal();
                                }}
                            >
                                Disable
                            </Button>
                        </div>
                    </ModalBody>
                )}
                {isDeleting && (
                    <ModalBody>
                        <div className="d-flex justify-content-center my-5">
                            <ThreeCircles
                                visible={isDeleting}
                                height="100"
                                width="100"
                                color="#ff3d60"
                                ariaLabel="three-circles-loading"
                                wrapperStyle={{ margin: "0 auto" }}
                                wrapperClass="justify-content-center"
                            />
                        </div>
                    </ModalBody>
                )}
            </Modal>

            {/* Activate Modal */}
            <Modal isOpen={activateModal} toggle={() => toggleActivateModal()} centered>
                {!isActivating && (
                    <ModalBody>
                        <div className="mt-2 text-center">
                            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
                                <h4>Activate User's Account</h4>
                                <p className="text-muted mx-4 mb-0">Are you sure you want to activate this user?</p>
                            </div>
                        </div>
                        <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
                            <Button
                                type="button"
                                color="light"
                                onClick={() => toggleActivateModal()}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                color="success"
                                onClick={async () => {
                                    await activateUser();
                                    toggleActivateModal();
                                }}
                            >
                                Activate
                            </Button>
                        </div>
                    </ModalBody>
                )}
                {isActivating && (
                    <ModalBody>
                        <div className="d-flex justify-content-center my-5">
                            <ThreeCircles
                                visible={isActivating}
                                height="100"
                                width="100"
                                color="#4192C3"
                                ariaLabel="three-circles-loading"
                                wrapperStyle={{ margin: "0 auto" }}
                                wrapperClass="justify-content-center"
                            />
                        </div>
                    </ModalBody>
                )}
            </Modal>

            {/* Create Modal */}
            <Modal isOpen={createModal} toggle={() => toggleCreateModal()} centered>
                {!isCreating && (
                    <form className="tablelist-form" onSubmit={handleCreateUser}>
                        <ModalHeader toggle={() => toggleCreateModal()}>Create User</ModalHeader>
                        <ModalBody>
                            {/* User Information */}
                            <FormGroup>
                                <Label htmlFor="create-first-name">First Name*</Label>
                                <Input
                                    type="text"
                                    name="first_name"
                                    id="create-first-name"
                                    placeholder="Richard"
                                    required
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label htmlFor="create-last-name">Last Name*</Label>
                                <Input
                                    type="text"
                                    name="last_name"
                                    id="create-last-name"
                                    placeholder="Ryan"
                                    required
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label htmlFor="create-email">Email Address*</Label>
                                <Input
                                    type="email"
                                    name="email_address"
                                    id="create-email"
                                    placeholder="rich@fish-trak.com"
                                    required
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label htmlFor="create-mobile-number">Mobile Number*</Label>
                                <Input
                                    type="tel"
                                    name="mobile_number"
                                    id="create-mobile-number"
                                    placeholder="+14151234567"
                                    required
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label htmlFor="create-avatar">User Avatar*</Label>
                                <Row>
                                    <Col xs="12">
                                        <Input
                                            type="file"
                                            name="image_link"
                                            id="create-avatar"
                                            className="form-control"
                                            onChange={(e) => { setUploadedImage(e.target.files[0]); }}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>
                            <FormGroup>
                                <Label htmlFor="create-home-port">Home Port Location*</Label>
                                <Input
                                    type="text"
                                    name="home_port"
                                    id="create-home-port"
                                    placeholder="Toms River"
                                    required
                                />
                            </FormGroup>
                            <Row>
                                <Col>
                                    <FormGroup>
                                        <Label htmlFor="create-home-port-lat">Home Port Latitude*</Label>
                                        <Input
                                            type="number"
                                            name="home_port_lat"
                                            id="create-home-port-lat"
                                            placeholder="Enter Latitude*"
                                            step="any"
                                            required
                                        />
                                    </FormGroup>
                                </Col>
                                <Col>
                                    <FormGroup>
                                        <Label htmlFor="create-home-port-lng">Home Port Longitude*</Label>
                                        <Input
                                            type="number"
                                            name="home_port_lng"
                                            id="create-home-port-lng"
                                            placeholder="Enter Longitude*"
                                            step="any"
                                            required
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <FormGroup>
                                <Label htmlFor="create-user-country">User Country*</Label>
                                <Input
                                    type="select"
                                    name="user_country"
                                    id="create-user-country"
                                    required
                                >
                                    <option value="">Select User Country</option>
                                    <option value="USA">United States</option>
                                    <option value="CAN">Canada</option>
                                    {/* Add more countries as needed */}
                                </Input>
                            </FormGroup>

                            {/* Units System */}
                            <FormGroup>
                                <Label>Units System*</Label>
                                <div>
                                    <FormGroup check inline>
                                        <Label check>
                                            <Input
                                                type="radio"
                                                name="units"
                                                value="true"
                                                defaultChecked={true}
                                            />{' '}
                                            Metric
                                        </Label>
                                    </FormGroup>
                                    <FormGroup check inline>
                                        <Label check>
                                            <Input
                                                type="radio"
                                                name="units"
                                                value="false"
                                                defaultChecked={false}
                                            />{' '}
                                            Imperial
                                        </Label>
                                    </FormGroup>
                                </div>
                            </FormGroup>

                            {/* Permissions */}
                            <FormGroup>
                                <Label>Permissions*</Label>
                                <div>
                                    {/*<FormGroup check inline>
                                        <Label check>
                                            <Input
                                                type="checkbox"
                                                name="partner_access"
                                            />{' '}
                                            Partner Access
                                        </Label>
                                    </FormGroup>*/}
                                    <FormGroup check inline>
                                        <Label check>
                                            <Input
                                                type="checkbox"
                                                name="privacy_mode"
                                            />{' '}
                                            Privacy Mode
                                        </Label>
                                    </FormGroup>
                                </div>
                            </FormGroup>

                            {/* Partner Tag Program */}
                            {/*allTagPrograms.length > 0 && (
                                <FormGroup>
                                    <Label htmlFor="create-tag-program">Partner Tag Program*</Label>
                                    <Input
                                        type="select"
                                        name="tag_program"
                                        id="create-tag-program"
                                        required
                                    >
                                        <option value="">Select Tag Program</option>
                                        {allTagPrograms.map((prog) => (
                                            <option key={prog.id} value={prog.id}>
                                                {prog.name}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            )*/}

                            {/* Image Preview */}
                            {uploadedImage && (
                                <FormGroup>
                                    <Label htmlFor="create-avatar-preview">Avatar Preview:</Label>
                                    <img
                                        className="avatar-sm rounded-circle"
                                        alt="Avatar Preview"
                                        src={URL.createObjectURL(uploadedImage)}
                                    />
                                </FormGroup>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <div className="hstack gap-2 justify-content-end">
                                <Button
                                    type="button"
                                    color="secondary"
                                    onClick={() => toggleCreateModal()}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    color="success"
                                    disabled={isUploading}
                                >
                                    Create
                                </Button>
                            </div>
                        </ModalFooter>
                    </form>
                )}
                {/* Loader for Create */}
                {isCreating && (
                    <ModalBody>
                        <div className="d-flex justify-content-center my-5">
                            <ThreeCircles
                                visible={isCreating}
                                height="100"
                                width="100"
                                color="#4192C3"
                                ariaLabel="three-circles-loading"
                                wrapperStyle={{ margin: "0 auto" }}
                                wrapperClass="justify-content-center"
                            />
                        </div>
                    </ModalBody>
                )}
            </Modal>

        </React.Fragment>
    );

};

export default ProgramUsers;